/**
 * MetroBadge — Tiny metro indicator with in-place picker.
 *
 * Click → dropdown to switch metro without leaving the current page.
 * "Change metro…" still nukes localStorage and routes home for users who
 * want a clean reset (rare).
 */
import { useEffect, useRef, useState } from 'react';
import { visibleMetroOptions } from '../../config/cityRows';
import { useMetroStore } from '../../stores/metroStore';
import { rememberMetro } from '../../stores/metroAccount';
import { useNavigate } from 'react-router-dom';

export default function MetroBadge({ className = '' }: { className?: string }) {
  const selectedMetro = useMetroStore((s) => s.selectedMetro);
  const getMetroLabel = useMetroStore((s) => s.getMetroLabel);
  const clearMetro = useMetroStore((s) => s.clearMetro);
  const metros = useMetroStore((s) => s.metros);
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement | null>(null);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const onDocClick = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [open]);

  if (!selectedMetro) return null;

  // Same list the home hero uses. An empty store means the metros API
  // has not loaded, and the launched rows stand in — including Houston
  // and San Antonio, which the old two-city fallback left out.
  const knownMetros = visibleMetroOptions(metros);

  const handleSwitch = (metroKey: string) => {
    if (metroKey !== selectedMetro) {
      rememberMetro(metroKey);
      // Soft refresh of any keyed-by-metro caches: trigger a route-state nudge
      // so consumers using URL search params recompute. We don't need a full
      // page reload — Zustand persist + subscribed hooks pick the new value up.
      window.dispatchEvent(new CustomEvent('metro:changed', { detail: { metro: metroKey } }));
    }
    setOpen(false);
  };

  const handleClearAndGoHome = () => {
    clearMetro();
    setOpen(false);
    navigate('/');
  };

  return (
    <div className={`relative inline-block ${className}`} ref={wrapperRef}>
      <button
        onClick={() => setOpen((o) => !o)}
        className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium transition-colors cursor-pointer"
        style={{ background: 'transparent', border: '1px solid var(--ts-accent-primary)', color: 'inherit' }}
        title="Switch metro"
        data-testid="metro-badge"
        aria-haspopup="menu"
        aria-expanded={open}
      >
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
          <circle cx="12" cy="10" r="3" />
        </svg>
        {getMetroLabel()}
        <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className={`transition-transform ${open ? 'rotate-180' : ''}`}>
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </button>

      {open && (
        <div
          role="menu"
          data-testid="metro-badge-menu"
          className="absolute right-0 mt-1 z-50 min-w-[180px] rounded-lg border border-[color:var(--ts-border-subtle)] bg-[color:var(--ts-bg-elevated)] shadow-lg overflow-hidden"
        >
          {knownMetros.map((m) => {
            const active = m.key === selectedMetro;
            return (
              <button
                key={m.key}
                role="menuitem"
                data-testid={`metro-option-${m.key.toLowerCase()}`}
                onClick={() => handleSwitch(m.key)}
                className={`w-full text-left px-3 py-2 text-sm flex items-center justify-between
                  ${active
                    ? 'bg-[color:var(--ts-accent-muted)] text-[color:var(--ts-accent-text)] font-medium'
                    : 'text-[color:var(--ts-text-secondary)] hover:bg-[color:var(--ts-bg-inset)]'
                  }`}
              >
                <span>{m.label}</span>
                {active && (
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                )}
              </button>
            );
          })}
          <button
            role="menuitem"
            data-testid="metro-clear"
            onClick={handleClearAndGoHome}
            className="w-full text-left px-3 py-2 text-xs border-t border-[color:var(--ts-border-subtle)] text-[color:var(--ts-text-secondary)] hover:bg-[color:var(--ts-bg-inset)]"
          >
            Reset & choose again
          </button>
        </div>
      )}
    </div>
  );
}
